import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { bulkEditTargetIds } from './assignmentLineHighlight';
import { fulfillmentProductKey } from './bulkFulfillmentOverride';
import {
  assignmentLineHighlightSession,
  type AssignmentLineHighlightSession,
} from './assignmentLineHighlightSession';

export type SyncAssignmentLinesForBulkEditOptions = {
  /**
   * When false, skip syncing (e.g. assignment refetch with no rows yet).
   * Keeps the prior map so bulk Planning Details still resolves highlighted lines.
   */
  linesReady?: boolean;
};

/** Board lines keyed by suggestion id for bulk modal overrides (GEN-2958). */
export function syncAssignmentLinesForBulkEdit(
  cartKey: string,
  lines: readonly AssignmentLine[],
  options: SyncAssignmentLinesForBulkEditOptions = {},
): void {
  const linesReady = options.linesReady ?? true;
  if (!linesReady) return;
  const session = assignmentLineHighlightSession(cartKey);
  const next = new Map<string, AssignmentLine>();
  for (const line of lines) {
    next.set(line.suggestionId, line);
  }
  session.linesBySuggestionId = next;
}

export function assignmentBulkEditCartKey(
  supplierId: string | undefined,
  shipToWarehouseId: string | null | undefined,
): string | null {
  if (supplierId == null) return null;
  return `${supplierId}:${shipToWarehouseId ?? ''}`;
}

export function bulkEditScope(
  session: AssignmentLineHighlightSession,
  originSuggestionId: string,
): {
  isBulk: boolean;
  count: number;
  targetIds: readonly string[];
  lines: AssignmentLine[];
} {
  const targetIds = bulkEditTargetIds(
    session.highlightedIds,
    originSuggestionId,
  );
  const lines: AssignmentLine[] = [];
  for (const id of targetIds) {
    const line = session.linesBySuggestionId.get(id);
    if (line != null) lines.push(line);
  }
  return {
    isBulk: targetIds.length > 1,
    count: targetIds.length,
    targetIds,
    lines,
  };
}

export function bulkLeadTimeSeriesTargets(
  session: AssignmentLineHighlightSession,
  originSuggestionId: string,
): {
  highlightedLineCount: number;
  series: ReadonlyArray<{ warehouseId: string; productV3Key: string }>;
} {
  const { count, lines } = bulkEditScope(session, originSuggestionId);
  const seen = new Set<string>();
  const series: Array<{ warehouseId: string; productV3Key: string }> = [];
  for (const line of lines) {
    const productV3Key = fulfillmentProductKey(line);
    const warehouseId = line.destinationWarehouseId;
    if (productV3Key == null || warehouseId == null) continue;
    const key = `${productV3Key}:${warehouseId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    series.push({ productV3Key, warehouseId });
  }
  return { highlightedLineCount: count, series };
}

export function readBulkEditScope(
  cartKey: string | null,
  originSuggestionId: string,
): ReturnType<typeof bulkEditScope> & { cartKey: string | null } {
  if (cartKey == null) {
    return {
      cartKey: null,
      isBulk: false,
      count: 1,
      targetIds: [originSuggestionId],
      lines: [],
    };
  }
  const session = assignmentLineHighlightSession(cartKey);
  return { cartKey, ...bulkEditScope(session, originSuggestionId) };
}
